'use client';

import {computed, observable} from 'mobx';
import {SearchSuggestionCollection} from '../collections/SearchSuggestionCollection';
import {SearchSuggestionModel} from './SearchSuggestionModel';

const SUGGESTIONS_URL_NAME = 'ajax_gallery_suggestions';

export class IndexSearchModel {
  @observable accessor ready = false;

  categories = [];

  suggestions = [];

  hints = [];

  templates = [];

  constructor(attributes = {}) {
    Object.assign(this, this.parse(attributes));

    this.categoryCollection = new SearchSuggestionCollection(this.categories);
    this.suggestionCollection = new SearchSuggestionCollection(this.suggestions);
    this.hintCollection = new SearchSuggestionCollection(this.suggestions);
    this.templateCollection = new SearchSuggestionCollection(this.templates);
    this.urlName = SUGGESTIONS_URL_NAME;
    this.tracking = '';
  }

  findWhere(options) {
    return (
      (options && this.categoryCollection.findWhere(options)) ||
      this.suggestionCollection.findWhere(options) ||
      this.hintCollection.findWhere(options) ||
      this.templateCollection.findWhere(options) ||
      null
    );
  }

  findIndexWhere(propsOrFunction) {
    if (!propsOrFunction) {
      return -1;
    }

    let index = this.categoryCollection.findIndexWhere(propsOrFunction);
    if (index !== -1) {
      return index;
    }

    index = this.suggestionCollection.findIndexWhere(propsOrFunction);
    if (index !== -1) {
      return index + this.categoryCollection.length;
    }

    index = this.hintCollection.findIndexWhere(propsOrFunction);
    if (index !== -1) {
      return index + this.categoryCollection.length + this.suggestionCollection.length;
    }

    index = this.templateCollection.findIndexWhere(propsOrFunction);
    if (index !== -1) {
      return (
        index +
        this.categoryCollection.length +
        this.suggestionCollection.length +
        this.hintCollection.length
      );
    }

    return -1;
  }

  parse(json = {}) {
    const parsedJson = {...json};

    parsedJson.search = json.search || '';
    parsedJson.status = json.status || 'unknown';
    parsedJson.tracking = json.tracking || '';

    const categories = (parsedJson.categories = (json.categories || json.category || []).concat());
    const suggestions = (parsedJson.suggestions = (json.suggestions || []).concat());
    const hints = (parsedJson.hints = (json.hints || []).concat());
    const templates = (parsedJson.templates = (json.templates || []).concat());

    // CATEGORY SUGGESTIONS
    let {length} = categories;
    for (let i = 0; i < length; ++i) {
      const categoryData = categories[i];
      categories[i] = new SearchSuggestionModel({
        id: categoryData.id,
        text: categoryData.text,
        type: SearchSuggestionModel.TYPE_CATEGORY,
      });
    }

    // KEYWORD SUGGESTIONS
    length = suggestions.length;
    for (let i = 0; i < length; ++i) {
      const suggestionData = suggestions[i];
      let text = '';
      let id = '';
      let url = '';

      if (suggestionData instanceof Object) {
        id = suggestionData.id;
        text = suggestionData.text;
        url = suggestionData.url;
      } else {
        id = text = suggestionData;
      }

      suggestions[i] = new SearchSuggestionModel({
        id,
        text,
        type: SearchSuggestionModel.TYPE_SUGGESTION,
        url,
      });
    }

    // HINT SUGGESTIONS
    length = hints.length;
    for (let i = 0; i < length; ++i) {
      const hintData = hints[i];
      hints[i] = new SearchSuggestionModel({
        id: hintData.id,
        text: `${hintData.text} ("${hintData.id}"?)`,
        type: SearchSuggestionModel.TYPE_HINT,
      });
    }

    // TEMPLATE SUGGESTIONS
    length = templates.length;
    for (let i = 0; i < length; ++i) {
      const templateData = templates[i];
      templates[i] = new SearchSuggestionModel({
        id: templateData.id,
        text: templateData.text,
        type: SearchSuggestionModel.TYPE_TEMPLATE,
        src: templateData.image,
        data: templateData.template,
      });
    }

    return parsedJson;
  }

  @computed
  get numSuggestions() {
    return (
      this.categoryCollection.length +
      this.suggestionCollection.length +
      this.hintCollection.length +
      this.templateCollection.length
    );
  }

  @computed
  get noMatches() {
    return this.ready && this.numSuggestions === 0;
  }

  async sync(json) {
    this.ready = false;
    Object.assign(this, this.parse(json));
    this.categoryCollection.reset(this.categories);
    this.suggestionCollection.reset(this.suggestions);
    this.hintCollection.reset(this.hints);
    this.templateCollection.reset(this.templates);
    this.ready = true;
  }
}
